import moment from 'moment';
import { Grid, TextField, Autocomplete, Checkbox, FormGroup, CircularProgress, Box, Typography, InputAdornment, Tooltip, Chip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import SectionDivider from './SectionDivider';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import { regexConstants } from '../../../constants/regEx';
import LessMoreTextFieldComp from '../../../shared_elements/components/LessMoreTextFieldComp';
import { numberWithCommas, formatCycles, getTextFieldRows, roundAmount } from '../../../utils';

const ShopVisitField = ({ modeType, assetData, dropdownLoader, llpModuleData, llpList, pltConstants, shopVisit, onFieldChange, onCheckBoxChange, onAmountChange, onFocusChange, error, isLoading, totalSvCost }) => {
    const effectiveAssetType = assetData?.asset_type === 1 ? shopVisit?.major_assembly?.asset_type : assetData?.asset_type;
    return (
        <>
            <Grid container spacing={2}>
                {assetData?.asset_type === 1 ? <Grid size={{ md: 4, xs: 12 }}>
                    <Autocomplete
                        disablePortal
                        disableClearable
                        options={pltConstants?.asset_types || []}
                        disabled={modeType === 'view' || isLoading?.buttonLoader}
                        getOptionLabel={(option) => `${option?.asset_name || ""} ${option?.name || ""}`}
                        value={shopVisit?.major_assembly || null}
                        onChange={(e, value) => { onFieldChange("major_assembly", value); }}
                        onFocus={() => onFocusChange?.("major_assembly")}
                        noOptionsText={ modeType !== "view" && dropdownLoader?.major_assembly ? "Loading..." : "No Option Available" }
                        renderInput={(params) => {
                            const selectedLabel = params?.inputProps?.value || "";
                            return (
                                <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow >
                                    <TextField
                                        required
                                        {...params}
                                        label="Assembly"
                                        margin='normal'
                                        size='small'
                                        error={Boolean(error?.major_assembly)}
                                        helperText={error?.major_assembly}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true,
                                            },
                                            input: {
                                                ...params?.InputProps, endAdornment: (<>{dropdownLoader?.major_assembly ? (<CircularProgress color="inherit" size={20} />) : null}
                                                    {params?.InputProps?.endAdornment}
                                                </>
                                                )
                                            }
                                        }}
                                        disabled={modeType === 'view' || isLoading?.buttonLoader}
                                    />
                                </Tooltip>)
                        }}
                    />
                </Grid> : null}

                <Grid size={{ md: 4, xs: 12 }}>
                    <Autocomplete
                        disablePortal
                        disableClearable
                        multiple={effectiveAssetType === 2}
                        options={pltConstants?.maintenance_events || []}
                        loading={dropdownLoader?.maintenance_check}
                        noOptionsText={dropdownLoader?.maintenance_check ? "Loading..." : "No Option Available"}
                        disabled={modeType === 'view' || dropdownLoader?.maintenance_check || isLoading?.buttonLoader}
                        sx={{
                            "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap !important" },
                            "& .MuiAutocomplete-input": modeType === 'view' && effectiveAssetType === 2 ? { flex: "0 0 0px !important", minWidth: "0 !important", width: "0 !important", padding: "0 !important" } : { minWidth: 0 },
                        }}
                        getOptionLabel={(option) => option.name || ""}
                        value={effectiveAssetType === 2 ? (shopVisit?.maintenance_check || []) : (shopVisit?.maintenance_check || null)}
                        onChange={(e, value) => {
                            if (effectiveAssetType === 2 && Array.isArray(value)) {
                                if (value.length > 2) return;
                                const llpSelected = value.filter((opt) => opt?.is_llp_event === true);
                                const nonLlpSelected = value.filter((opt) => opt?.is_llp_event === false);
                                if (llpSelected.length > 1 || nonLlpSelected.length > 1) return;
                            }
                            onFieldChange("maintenance_check", value);
                        }}
                        getOptionDisabled={(option) => {
                            if (effectiveAssetType !== 2) return false;
                            const selected = Array.isArray(shopVisit?.maintenance_check) ? shopVisit.maintenance_check : [];
                            if (selected.some((sel) => sel?.id === option?.id)) return false;
                            return selected.some((sel) => sel?.is_llp_event === option?.is_llp_event);
                        }}
                        onFocus={() => onFocusChange?.("maintenance_check")}
                        renderTags={(tagValue, getTagProps) => (
                            <Box sx={{ display: "flex", flexWrap: "nowrap", overflow: "hidden", width: "100%", gap: 0.5 }}>
                                {tagValue.map((option, index) => {
                                    const { key, onDelete, ...tagProps } = getTagProps({ index });
                                    const label = option?.name || "";
                                    return (
                                        <Tooltip key={key} title={label}>
                                            <Box component="span" sx={{ minWidth: 0, flex: "1 1 0", display: "inline-block" }}>
                                                <Chip
                                                    label={label}
                                                    size="small"
                                                    disabled={modeType === 'view'}
                                                    {...(modeType !== 'view' ? { onDelete } : {})}
                                                    sx={{
                                                        minWidth: 0,
                                                        width: "100%",
                                                        "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                                                    }}
                                                    {...tagProps}
                                                />
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        )}
                        renderInput={(params) => {
                            const selectedLabel = params?.inputProps?.value || "";
                            return (
                                <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow >
                                    <TextField
                                        required
                                        {...params}
                                        label="Shop Visit Event"
                                        margin='normal'
                                        size='small'
                                        error={Boolean(error?.maintenance_check)}
                                        helperText={error?.maintenance_check}
                                        slotProps={{
                                            inputLabel: { shrink: true },
                                            input: {
                                                ...params?.InputProps, endAdornment: (<>{dropdownLoader?.maintenance_check ? (<CircularProgress color="inherit" size={20} />) : null}
                                                    {params?.InputProps?.endAdornment}
                                                </>
                                                )
                                            }
                                        }}
                                        disabled={modeType === 'view' || dropdownLoader?.maintenance_check || isLoading?.buttonLoader}
                                    />
                                </Tooltip>
                            );
                        }}
                        renderOption={(props, option) => {
                            const label = option.name || "";
                            const isLongText = label.length > 23;
                            return (
                                <li {...props} key={label}>
                                    {isLongText ? (
                                        <Tooltip title={label} placement="right"><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70px", display: "inline-block", }} > {label} </span></Tooltip>
                                    ) : (label)}
                                </li>
                            );
                        }}
                    />
                </Grid>
                <Grid size={{ md: 4, xs: 12 }}>
                    <TextField
                        id="cost"
                        value={(modeType === 'view' || isLoading?.buttonLoader) && shopVisit?.cost !== '' && shopVisit?.cost !== null && shopVisit?.cost !== undefined ? numberWithCommas(roundAmount(shopVisit?.cost)) : (shopVisit?.cost ?? 0)}
                        onChange={(e, value) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange('cost', e.target.value.trimStart()) : e.preventDefault() }}
                        label="Shop Visit Cost"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                        fullWidth
                        disabled={modeType === 'view' || isLoading?.buttonLoader}
                    />
                </Grid>
                <Grid size={{ md: 4, xs: 12 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            label="Shop Visit Start Date"
                            value={shopVisit?.shop_visit_start_date ? moment(shopVisit.shop_visit_start_date) : null}
                            onChange={(data) => {
                                if (!data) { onFieldChange('shop_visit_start_date', null); }
                                else { onFieldChange('shop_visit_start_date', moment(data).format(backendDateFormat));onFocusChange?.("shop_visit_start_date") }
                            }}
                            slotProps={{
                                textField: {
                                    InputLabelProps: { shrink: true },
                                    size: "small",
                                    margin: "normal",
                                },
                                field: { clearable: true, onClear: () => { onFieldChange("shop_visit_start_date", null); } },
                            }}
                            sx={{ width: "100%" }}
                            format={fieldDateFormat}
                            disabled={modeType === 'view' || isLoading?.buttonLoader}
                            
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid size={{ md: 4, xs: 12 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            required
                            label="Shop Visit End Date"
                            value={shopVisit?.shop_visit_end_date ? moment(shopVisit.shop_visit_end_date) : null}
                            onChange={(data, value) => {onFieldChange('shop_visit_end_date', moment(data).format(backendDateFormat));onFocusChange?.("shop_visit_end_date")}}
                            // minDate={shopVisit?.shop_visit_start_date ? moment(shopVisit.shop_visit_start_date) : null}
                            slotProps={{
                                textField: {
                                    required: "true",
                                    InputLabelProps: { shrink: true },
                                    size: "small",
                                    margin: "normal",
                                    helperText: error?.shop_visit_end_date,
                                    error: error?.shop_visit_end_date,
                                    onFocus: () => { onFocusChange?.("shop_visit_end_date") }
                                },
                            }}
                            sx={{ width: "100%" }}
                            format={fieldDateFormat}
                            disabled={modeType === 'view' || isLoading?.buttonLoader}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid size={{ md: 4, xs: 12 }}>
                    <TextField
                        required={effectiveAssetType !== 4}
                        id="tsn"
                        value={(modeType === 'view' || isLoading?.buttonLoader) && shopVisit?.tsn !== '' && shopVisit?.tsn !== null && shopVisit?.tsn !== undefined ? numberWithCommas(roundAmount(shopVisit?.tsn)) : shopVisit?.tsn}
                        onChange={(e, value) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange('tsn', e.target.value.trimStart()) : e.preventDefault() }}
                        label="TSN @ Shop Visit"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        error={!!error?.tsn}
                        helperText={error?.tsn}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 10 } }}
                        onFocus={() => onFocusChange?.("tsn")}
                        fullWidth
                        disabled={modeType === 'view' || isLoading?.buttonLoader}
                    />
                </Grid>
                <Grid size={{ md: 4, xs: 12 }}>
                    <TextField
                        required={effectiveAssetType !== 5}
                        id="csn"
                        value={(modeType === 'view' || isLoading?.buttonLoader) && shopVisit?.csn !== '' && shopVisit?.csn !== null && shopVisit?.csn !== undefined ? formatCycles(shopVisit?.csn) : shopVisit?.csn}
                        onChange={(e, value) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange('csn', e.target.value.trimStart()) : e.preventDefault() }}
                        label="CSN @ Shop Visit"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        error={!!error?.csn}
                        helperText={error?.csn}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 10 } }}
                        onFocus={() => onFocusChange?.("csn")}
                        fullWidth
                        disabled={modeType === 'view' || isLoading?.buttonLoader}
                    />
                </Grid>
                {assetData?.asset_type === 1 && modeType !== 'view' ? (
                    <>
                        <Grid size={{ md: 4, xs: 12 }} />
                        <Grid size={{ md: 4, xs: 12 }} />
                    </>
                ) : null}
                {modeType === "view" && <Grid size={{ md: 4, xs: 12 }}>
                    <TextField
                        id="claim_approval_period"
                        value={shopVisit?.claim_approval_period ? shopVisit?.claim_approval_period : ""}
                        label="Claim Approval Period (Days)"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                        disabled
                    />
                </Grid>}
                {modeType === "view" && llpList?.length > 0 && <Grid size={{ md: 4, xs: 12 }}>
                    <TextField
                        id="total_sv_cost"
                        label="Engine LLP Shop Visit Cost"
                        value={numberWithCommas(roundAmount(totalSvCost ?? llpModuleData.reduce((sum, module) => shopVisit?.modules?.[module.value]?.checked ? sum + (Number(shopVisit?.modules?.[module.value]?.cost) || 0) : sum, 0)))}
                        variant="outlined"
                        size="small"
                        fullWidth
                        margin="normal"
                        slotProps={{ inputLabel: { shrink: true }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                        disabled
                    /></Grid>}
                {modeType === 'view' && <SectionDivider ></SectionDivider>}
                <Grid size={{ md: 6, xs: 12 }}>
                    <TextField
                        id="shop_visit_facility"
                        value={shopVisit?.shop_visit_facility}
                        onChange={(e, value) => regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("shop_visit_facility", e.target.value.trimStart()) : e.preventDefault()}
                        label="Shop Visit Facility"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 } }}
                        onFocus={() => onFocusChange?.("shop_visit_facility")}
                        fullWidth
                        disabled={modeType === 'view' || isLoading?.buttonLoader}
                        rows={getTextFieldRows(shopVisit?.shop_visit_facility, 3, 110)}
                        multiline
                    />
                </Grid>
                <Grid size={{ md: 6, xs: 12 }}>
                    <TextField
                        id="facility_location"
                        value={shopVisit?.facility_location}
                        onChange={(e, value) => regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("facility_location", e.target.value.trimStart()) : e.preventDefault()}
                        label="Shop Visit Location"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 } }}
                        onFocus={() => onFocusChange?.("facility_location")}
                        fullWidth
                        rows={getTextFieldRows(shopVisit?.facility_location, 3, 110)}
                        disabled={modeType === 'view' || isLoading?.buttonLoader}
                        multiline
                    />
                </Grid>
                {modeType === "view" ? <Grid item size={{ md: 12, xs: 12 }}>
                    <LessMoreTextFieldComp label='Reason for Shop Visit' value={shopVisit?.removal_reason || ""} rows={3} />
                </Grid> :
                    <Grid size={{ md: 12, xs: 12 }}>
                        <TextField
                            required
                            name='removal_reason'
                            label='Reason for Shop Visit'
                            value={shopVisit?.removal_reason || ''}
                            fullWidth
                            margin="normal"
                            size="small"
                            multiline
                            rows={3}
                            onChange={(e) => onFieldChange('removal_reason', e.target.value)}
                            sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                            slotProps={{ inputLabel: { shrink: true } }}
                            variant='outlined'
                            error={error.removal_reason ? true : false}
                            onFocus={() => onFocusChange?.("removal_reason")}
                            helperText={error.removal_reason || ''}
                            disabled={modeType === 'view' || isLoading?.buttonLoader}
                        />
                    </Grid>}
                {modeType === "view" ? <Grid item size={{ md: 12, xs: 12 }}>
                    <LessMoreTextFieldComp label='Remarks' value={shopVisit?.workscope_details || ""} />
                </Grid> :
                <Grid size={{ md: 12, xs: 12 }}>
                    <TextField
                        label="Shop Visit Workscope / Details"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                        value={shopVisit?.workscope_details || ""}
                        onChange={(e, value) => regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("workscope_details", e.target.value.trimStart()) : e.preventDefault()}
                        fullWidth
                        multiline
                        rows={3}
                        disabled={modeType === 'view' || isLoading?.buttonLoader}
                    />
                </Grid>}
            </Grid>
            <Grid container spacing={0} size={{ md: 12, xs: 12 }}  >
                {(effectiveAssetType !== 2 || (Array.isArray(shopVisit?.maintenance_check) && shopVisit.maintenance_check.some((ele) => ele?.is_llp_event === false))) ?
                    <Grid size={{ md: 3, xs: 12 }} sx={{ p: 0, m: 0 }}>
                        <FormGroup className='disabled-checkbox-wrapper' sx={{ m: 0, p: 0 }}>
                            <Box className="check-item-div" sx={{cursor: modeType === "view" ? "not-allowed" : "pointer"}}>
                                <Checkbox sx={{ paddingLeft: 0 }} disableRipple checked={!!shopVisit?.prsv} onChange={(e) => onFieldChange('prsv', e.target.checked)} disabled={modeType === 'view' || isLoading?.buttonLoader} />
                                <Typography className="check-item-span" fontSize='14px'>PRSV Event</Typography>
                            </Box>
                        </FormGroup>
                    </Grid>
                    : null}
            </Grid>
            {dropdownLoader?.engine_llp ? <CircularProgress color='primary' size={20} sx={{mt:1}} /> :
                llpModuleData?.length > 0 && effectiveAssetType === 2 && shopVisit?.prsv && (modeType !== 'view' || Object.keys(shopVisit?.modules || {}).length > 0) ?
                <Grid size={{ md: 12, xs: 12 }} sx={{ mt: 1 }}>
                    <Typography className='section-header-text'><u>Engine PRSV Shop Visit Cost Split Details:</u></Typography>
                    <Box className="escalation-container">
                        <Box className="escalation-box">
                            <Box sx={{ display: "flex", flexWrap: "wrap", rowGap: 1.5, columnGap: 3 }}>
                                {llpModuleData?.map(module => {
                                    const isChecked = Boolean(shopVisit?.modules?.[module.value]?.checked);
                                    return (
                                        <FormGroup className='disabled-checkbox-wrapper' key={`${module.value}-${module?.isLlp ? 'llp' : 'non_llp'}`}>
                                            <Box className="check-item-div" sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap", cursor: modeType === "view" ? "not-allowed" : "pointer" }}>
                                                <Checkbox
                                                    size="small"
                                                    sx={{ flexShrink: 0 }}
                                                    checked={isChecked}
                                                    onChange={(e) => onCheckBoxChange(module, e.target.checked)}
                                                    disabled={modeType === 'view'} />
                                                <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, mr: 1 }}>
                                                    <Typography className="check-item-span" fontSize="12px" sx={{ whiteSpace: "nowrap" }}>{module?.label}</Typography>
                                                    {!module?.isLlp ? <Chip label="Non LLP" size="small" variant="outlined" sx={{ ml: 0.5, fontSize: '0.65rem', height: '18px', flexShrink: 0, background: '#ebebeb' }} /> : null}
                                                </Box>
                                                {isChecked ? (
                                                    <TextField
                                                        placeholder="Amount"
                                                        variant="outlined"
                                                        size="small"
                                                        margin="normal"
                                                        value={modeType === 'view' && shopVisit?.modules?.[module.value]?.cost !== '' && shopVisit?.modules?.[module.value]?.cost !== null && shopVisit?.modules?.[module.value]?.cost !== undefined ? numberWithCommas(roundAmount(shopVisit?.modules?.[module.value]?.cost)) : (shopVisit?.modules?.[module.value]?.cost ?? '')}
                                                        onChange={(e) => regexConstants.decimalnodotstart.test(e.target.value) || e.target.value === '' ? onAmountChange(module, e.target.value.trimStart()) : e.preventDefault()}
                                                        slotProps={{ input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) },htmlInput: { maxLength: 11 } }}
                                                        sx={{ width: 150, flexShrink: 0 }}
                                                        disabled={modeType === 'view'}
                                                    />
                                                ) : null}
                                            </Box>
                                        </FormGroup>
                                    )
                                })}
                            </Box>
                        </Box>
                    </Box>
                </Grid> : null}
        </>
    )
}

export default ShopVisitField